/**
 * "Needs action": every flagged client, worst first, with recently-chased ones
 * dimmed and pushed to the bottom.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { daysAgo } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { Card, CardHead, EmptyState, Avatar } from './Primitives.jsx';

export default function QueueCard({ limit = 60 }) {
  const { rows, derive, setSheet, logContact, openDraft } = useApp();
  /* The header counts every flagged client, but only `limit` were rendered and
     nothing said so - the list just stopped part-way down. */
  const [shown, setShown] = useState(limit);
  const [open, setOpen] = useState(false);

  /* The whole book, sorted — kept until the book or the settings change.
     It was rebuilt on every render of the dashboard. */
  const items = useMemo(() => rows
    .map(r => ({ r, d: derive(r) }))
    .filter(x => x.d.flags.length)
    .map(x => ({ ...x, sev: x.d.flags[0].sev }))
    .sort((a, b) =>
      (a.d.snoozed - b.d.snoozed)
      || (b.sev - a.sev)
      || String(a.r.name).localeCompare(String(b.r.name))), [rows, derive]);

  const pending = items.filter(i => !i.d.snoozed).length;
  /* The header badge counts only what still needs chasing, but the list also
     carries the recently-chased ones, dimmed at the bottom. Saying so stops
     the two numbers looking like a mistake. */
  const snoozed = items.length - pending;

  return (
    <Card>
      <CardHead icon="alert" tone="g-red" title="Needs action" count={pending}>
        {/* Folded to begin with. Four hundred flagged clients unrolled under
            the dashboard made every other block on the page something you
            had to scroll past them to reach, and the number in the heading
            is what most visits came for. */}
        <button type="button" className="btn sm" onClick={() => setOpen(o => !o)}>
          <Icon name="chev" className={open ? 'up' : ''} />
          {open ? 'Hide' : `Show ${pending}`}
        </button>
      </CardHead>

      {open && items.length ? (
        <div className="qlist">
          {items.slice(0, shown).map(({ r, d, sev }) => (
            <div
              key={r.id}
              className={`q${d.snoozed ? ' snooze' : ''}`}
              /* Looking at the list is a reading action; the view carries an
                 "Edit client" button for when a change is actually wanted. */
              onClick={() => setSheet({ kind: 'clientView', id: r.id })}
            >
              <div className={`sev s${sev}`} />
              <Avatar name={r.name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{r.name || 'Untitled'}</b>
                <div className="why">
                  {d.flags.map(f => f.txt).join(' · ')}
                  {d.snoozed ? ` · chased ${daysAgo(d.con)}d ago` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="btn sm"
                  title="Mark as contacted — after you have called or emailed them"
                  aria-label="Mark as contacted"
                  onClick={e => { e.stopPropagation(); logContact([r.id]); }}
                >
                  <Icon name="check" />
                </button>
                <button
                  type="button"
                  className="btn sm"
                  title="Draft an email with AI"
                  aria-label="Draft an email with AI"
                  onClick={e => { e.stopPropagation(); openDraft({ kind: 'client', id: r.id }); }}
                >
                  <Icon name="mail" />
                </button>
              </div>
            </div>
          ))}

          {items.length > shown ? (
            <div className="qmore">
              <span>
                Showing <b>{shown}</b> of <b>{items.length}</b>
                {snoozed ? <> · <b>{snoozed}</b> chased recently</> : null}
              </span>
              <button type="button" className="btn sm" onClick={() => setShown(n => n + limit)}>
                Show {Math.min(limit, items.length - shown)} more
              </button>
            </div>
          ) : (
            <div className="qmore done">
              All {items.length} shown
              {snoozed ? ` · ${snoozed} chased recently, listed last` : ''}
            </div>
          )}
        </div>
      ) : !items.length ? (
        <EmptyState icon="check" tone="g-green" title="All clear">
          Trials, payments and renewals are in good standing.
        </EmptyState>
      ) : (
        /* Folded: one line saying what is in there, so the card is not a
           heading with nothing under it. */
        <button type="button" className="qfold" onClick={() => setOpen(true)}>
          <b>{pending}</b> client{pending === 1 ? '' : 's'} need chasing
          {snoozed ? <> · <b>{snoozed}</b> chased recently</> : null}
          <span className="qfoldgo">Open<Icon name="chev" /></span>
        </button>
      )}
    </Card>
  );
}
